import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { apiResponse, apiError } from '../schemas/common.schema';

const c = initContract();
const dateish = z.union([z.string(), z.date()]).optional();

export const CourseResponseSchema = z.object({
  _id: z.string(),
  course_owner_account_id: z.string().optional(),
  course_title: z.string().optional(),
  course_locale: z.string().optional(),
  course_category: z.string().optional(),
  course_sub_category: z.string().optional(),
  course_lessons_amount: z.number().optional(),
  course_minutes: z.number().optional(),
  course_description: z.string().optional(),
  course_objectives: z.array(z.string()).optional(),
  course_image: z.string().optional(),
  course_logo: z.string().optional(),
  course_version: z.string().optional(),
  course_optimized: z.boolean().optional(),
  course_is_published: z.boolean().optional(),
  course_is_public: z.boolean().optional(),
  course_url_token: z.string().optional(),
  course_lessons_studied: z.number().optional(),
  course_rating: z.number().optional(),
  course_created_at: dateish,
  course_modified_at: dateish,
});

const LessonSummaryInCourseSchema = z.object({
  _id: z.string(),
  lesson_title: z.string().optional(),
  lesson_summary: z.string().optional(),
  lesson_objectives: z.array(z.string()).optional(),
  lesson_token: z.string().optional(),
  lesson_order: z.number().optional(),
  lesson_minutes: z.number().optional(),
  lesson_topic: z.string().optional(),
  lesson_created_at: dateish,
  lesson_modified_at: dateish,
  course_id: z.string().optional(),
});

const CourseAuthorResponseSchema = z.object({
  _id: z.string(),
  author_name: z.string(),
  author_description: z.string().optional(),
  author_photo: z.string().optional(),
});

export const CourseDetailsResponseSchema = CourseResponseSchema.extend({
  lessons: z.array(LessonSummaryInCourseSchema).optional(),
  author: CourseAuthorResponseSchema.optional(),
});

const ContentLanguageSchema = z.object({
  id: z.string(),
  label: z.string(),
  course_count: z.number(),
  is_default: z.boolean(),
});

const getCoursesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().optional(),
  course_is_published: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : v),
    z.boolean().optional(),
  ),
  course_owner_account_id: z.string().optional(),
  // Школа-підписник: обмежити вибірку курсами account_courses цієї школи (лендинги by/pl/am).
  account_id: z.string().optional(),
  course_url_token: z.string().optional(),
  course_category: z.string().optional(),
  course_locale: z.string().optional(),
  sort_by: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

export const coursesContract = c.router({
  getCourses: {
    method: 'GET',
    path: '/courses',
    query: getCoursesQuery,
    responses: { 200: apiResponse(z.array(CourseResponseSchema)) },
  },
  getCourseDetailsBySlug: {
    method: 'GET',
    path: '/courses/details/:slug',
    pathParams: z.object({ slug: z.string() }),
    responses: { 200: apiResponse(CourseDetailsResponseSchema), 404: apiError },
  },
  getPopularCourses: {
    method: 'GET',
    path: '/courses/popular',
    query: z.object({ locale: z.string(), limit: z.coerce.number().optional() }),
    responses: { 200: apiResponse(z.array(CourseResponseSchema)) },
  },
  getContentLanguages: {
    method: 'GET',
    path: '/courses/content-languages',
    query: z.object({ category: z.string().optional(), account_id: z.string().optional() }),
    responses: { 200: apiResponse(z.array(ContentLanguageSchema)) },
  },
  getCategoriesByLocale: {
    method: 'GET',
    path: '/courses/categories-by-locale',
    query: z.object({ locale: z.string() }),
    responses: { 200: apiResponse(z.array(z.string())) },
  },
  getCourseById: {
    method: 'GET',
    path: '/courses/:course_id',
    pathParams: z.object({ course_id: z.string() }),
    responses: { 200: apiResponse(z.any()), 404: apiError },
  },
});

export type CourseResponseDto = z.infer<typeof CourseResponseSchema>;
export type CourseDetailsResponseDto = z.infer<typeof CourseDetailsResponseSchema>;
